/** Veronica's school clock. Minsk is UTC+3 year-round (no DST). */
export const SCHOOL_TIMEZONE = 'Europe/Minsk';

export const TALKALYST_URL = 'https://talkalyst.valeriugutu.com/';

/** Mon–Fri: after lessons, 21:00 until midnight. */
export const TALKALYST_WEEKDAY_HOUR = 21;

/** Sat–Sun: 17:00 until midnight. */
export const TALKALYST_WEEKEND_HOUR = 17;

const DISMISS_PREFIX = 'clara.talkalystNudge.dismissed.';

const WEEKDAYS = new Set(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);

export type SchoolClock = {
  dateKey: string;
  weekday: string;
  hour: number;
  isWeekday: boolean;
};

function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? '';
}

/** Calendar date, weekday, and hour in Europe/Minsk — never the process timezone. */
export function getSchoolClock(now: Date = new Date()): SchoolClock {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SCHOOL_TIMEZONE,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(now);

  const year = part(parts, 'year');
  const month = part(parts, 'month');
  const day = part(parts, 'day');
  const weekday = part(parts, 'weekday');
  const hour = Number.parseInt(part(parts, 'hour'), 10);

  return {
    dateKey: `${year}-${month}-${day}`,
    weekday,
    hour: Number.isFinite(hour) ? hour : 0,
    isWeekday: WEEKDAYS.has(weekday),
  };
}

/**
 * True in the reminder window: weekdays 21:00–23:59, weekends 17:00–23:59,
 * Europe/Minsk. Midnight starts a new day, so hour 0 is outside the window.
 */
export function isTalkalystNudgeWindow(now: Date = new Date()): boolean {
  const { isWeekday, hour } = getSchoolClock(now);
  const from = isWeekday ? TALKALYST_WEEKDAY_HOUR : TALKALYST_WEEKEND_HOUR;
  return hour >= from;
}

export function talkalystNudgeDismissKey(dateKey: string): string {
  return `${DISMISS_PREFIX}${dateKey}`;
}

export function isTalkalystNudgeDismissed(dateKey: string): boolean {
  try {
    return localStorage.getItem(talkalystNudgeDismissKey(dateKey)) === '1';
  } catch {
    return false;
  }
}

export function dismissTalkalystNudge(dateKey: string): void {
  try {
    localStorage.setItem(talkalystNudgeDismissKey(dateKey), '1');
  } catch {
    // Private mode / quota — hide for this session only.
  }
}
